<template>
  <div>
    <v-btn
      class="clone-trigger"
      rounded
      small
      :disabled="disabled"
      @click="openDialog">
      <v-icon
        small
        left>
        mdi-content-copy
      </v-icon>
      Clone to Countries
    </v-btn>

    <v-dialog
      v-model="dialog"
      max-width="560"
      :persistent="step === 'loading'"
      @click:outside="closeDialog">
      <v-card class="clone-card">
        <template v-if="step === 'select'">
          <div class="clone-header">
            <div>
              <div class="clone-title">
                Clone {{ entityTitle }}
              </div>
              <div class="clone-subtitle">
                {{ subtitle }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-icon
              class="clone-close"
              @click="closeDialog">
              mdi-close
            </v-icon>
          </div>

          <div class="clone-warning">
            <v-icon
              small
              color="#ad6800"
              class="mr-2">
              mdi-alert-outline
            </v-icon>
            <span>
              This replaces <strong>{{ brand }}</strong>'s {{ warningText }}
            </span>
          </div>

          <div class="clone-section">
            <div class="clone-label">
              SOURCE COUNTRY
            </div>
            <v-select
              v-model="source"
              :items="countries"
              item-text="country"
              item-value="code"
              color="secondary"
              outlined
              dense
              hide-details />
          </div>

          <div
            v-if="showDirtyNote"
            class="clone-note">
            Unsaved changes on this country won't be included — Save first.
          </div>

          <div class="clone-section-header">
            <div class="clone-label">
              DESTINATION COUNTRIES
            </div>
            <span
              v-if="selectedDest.length"
              class="clone-count">
              ({{ selectedDest.length }} selected)
            </span>
          </div>

          <v-text-field
            v-model="search"
            placeholder="Search country"
            prepend-inner-icon="mdi-magnify"
            color="secondary"
            outlined
            dense
            hide-details
            class="mb-3" />

          <div class="clone-dest-grid">
            <div
              v-for="country in destinationOptions"
              :key="country.code"
              class="clone-dest-row"
              :class="{ selected: selectedDest.includes(country.code) }"
              @click="toggleDest(country.code)">
              <div
                class="clone-checkbox"
                :class="{ checked: selectedDest.includes(country.code) }">
                <v-icon
                  v-if="selectedDest.includes(country.code)"
                  x-small
                  color="#fff">
                  mdi-check
                </v-icon>
              </div>
              <span>{{ country.country }}</span>
            </div>
            <div
              v-if="!destinationOptions.length"
              class="clone-dest-empty">
              No other countries available.
            </div>
          </div>

          <div class="clone-actions">
            <v-btn
              outlined
              rounded
              @click="closeDialog">
              Cancel
            </v-btn>
            <v-btn
              class="clone-submit"
              rounded
              :disabled="!source || !selectedDest.length"
              @click="submit">
              Clone to {{ selectedDest.length }} {{ selectedDest.length === 1 ? 'country' : 'countries' }}
            </v-btn>
          </div>
        </template>

        <template v-else-if="step === 'loading'">
          <div class="clone-loading">
            <v-progress-circular
              indeterminate
              size="44"
              width="3"
              color="#1a1a1a" />
            <div class="clone-loading-label">
              Cloning to {{ submittedDest.length }} {{ submittedDest.length === 1 ? 'country' : 'countries' }}…
            </div>
          </div>
        </template>

        <template v-else-if="step === 'confirm'">
          <div class="clone-result">
            <div class="clone-badge clone-badge-success">
              <v-icon color="#17976f">
                mdi-check
              </v-icon>
            </div>
            <div class="clone-result-title">
              Cloned to {{ submittedDest.length }} {{ submittedDest.length === 1 ? 'country' : 'countries' }}
            </div>
            <div class="clone-result-subtitle">
              {{ successLead }} copied from {{ sourceLabel }}. {{ successNote }}
            </div>
            <div class="clone-chips">
              <div
                v-for="code in submittedDest"
                :key="code"
                class="clone-chip">
                {{ countryLabel(code) }}
              </div>
            </div>
            <div class="clone-auto-close">
              This closes automatically…
            </div>
            <v-btn
              class="clone-submit"
              rounded
              @click="closeDialog">
              Done
            </v-btn>
          </div>
        </template>

        <template v-else-if="step === 'partial'">
          <div class="clone-result">
            <div class="clone-badge clone-badge-partial">
              <v-icon color="#ad6800">
                mdi-alert-outline
              </v-icon>
            </div>
            <div class="clone-result-title">
              Clone didn't finish
            </div>
            <div class="clone-result-subtitle">
              Some destinations may already be updated. Check each country against {{ sourceLabel }},
              then clone again — cloning twice is safe.
            </div>
            <div class="clone-chips">
              <div
                v-for="code in submittedDest"
                :key="code"
                class="clone-chip">
                {{ countryLabel(code) }}
              </div>
            </div>
            <div class="clone-error-actions">
              <v-btn
                outlined
                rounded
                @click="closeDialog">
                Done
              </v-btn>
              <v-btn
                class="clone-submit"
                rounded
                @click="retry">
                Retry
              </v-btn>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="clone-result">
            <div class="clone-badge clone-badge-error">
              <v-icon color="#d0362b">
                mdi-close
              </v-icon>
            </div>
            <div class="clone-result-title">
              Couldn't clone {{ entityTitle }}
            </div>
            <div class="clone-result-subtitle">
              {{ errorMessage }}
            </div>
            <div class="clone-error-actions">
              <v-btn
                outlined
                rounded
                @click="closeDialog">
                Cancel
              </v-btn>
              <v-btn
                v-if="errorAction === 'back'"
                class="clone-submit"
                rounded
                @click="backToSelection">
                Back to selection
              </v-btn>
              <v-btn
                v-else
                class="clone-submit"
                rounded
                @click="retry">
                Retry
              </v-btn>
            </div>
          </div>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'CloneToCountries',
  props: {
    cloneFn: {
      type: Function,
      required: true
    },
    entityTitle: {
      type: String,
      default: 'Product Display'
    },
    subtitle: {
      type: String,
      default: 'Copy the "Product to Show" list to other countries.'
    },
    warningText: {
      type: String,
      default: 'current product list at each destination with the source\'s — products not in the source are removed.'
    },
    successLead: {
      type: String,
      default: '"Product to Show" list'
    },
    successNote: {
      type: String,
      default: 'Please verify each destination.'
    },
    knownErrors: {
      type: Array,
      default: () => ([
        {
          message: 'No active source products to clone.',
          text: 'The source country has no active products to clone. Choose a different source, or save products there first.'
        }
      ])
    },
    unknownErrorIsPartial: {
      type: Boolean,
      default: false
    },
    brand: {
      type: String,
      default: ''
    },
    countries: {
      type: Array,
      default: () => []
    },
    currentCountry: {
      type: String,
      default: ''
    },
    sourceDirty: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: false,
      step: 'select',
      source: this.currentCountry,
      search: '',
      selectedDest: [],
      submittedDest: [],
      errorMessage: '',
      errorAction: 'retry'
    }
  },
  computed: {
    destinationOptions () {
      const search = this.search.trim().toLowerCase()

      return this.countries
        .filter((c) => c.code !== this.source)
        .filter((c) => !search || c.country.toLowerCase().includes(search))
    },
    showDirtyNote () {
      return this.source === this.currentCountry && this.sourceDirty
    },
    sourceLabel () {
      return this.countryLabel(this.source)
    }
  },
  watch: {
    dialog (val) {
      if (!val) {
        clearTimeout(this.autoCloseTimer)
      }
    },
    source (val) {
      this.selectedDest = this.selectedDest.filter((code) => code !== val)
    }
  },
  beforeDestroy () {
    clearTimeout(this.autoCloseTimer)
  },
  methods: {
    countryLabel (code) {
      return this.countries.find((c) => c.code === code)?.country || code
    },
    openDialog () {
      clearTimeout(this.autoCloseTimer)
      this.step = 'select'
      this.source = this.currentCountry
      this.search = ''
      this.selectedDest = []
      this.submittedDest = []
      this.errorMessage = ''
      this.errorAction = 'retry'
      this.dialog = true
    },
    closeDialog () {
      if (this.step === 'loading') {
        return
      }

      this.dialog = false
    },
    toggleDest (code) {
      this.selectedDest = this.selectedDest.includes(code)
        ? this.selectedDest.filter((c) => c !== code)
        : [...this.selectedDest, code]
    },
    mapError (error) {
      const known = this.knownErrors.find((e) => e.message === error?.message)

      if (known) {
        return {
          message: known.text,
          action: 'back'
        }
      }

      const message = `Something went wrong while cloning the ${this.entityTitle}. Please try again.`

      if (error?.statusCode === 400) {
        return {
          message,
          action: 'retry'
        }
      }

      return {
        message,
        action: this.unknownErrorIsPartial ? 'partial' : 'retry'
      }
    },
    async runClone () {
      try {
        await this.cloneFn({
          sourceCountry: this.source,
          targetCountries: this.submittedDest,
          brand: this.brand
        })

        this.step = 'confirm'
        this.$emit('cloned', [...this.submittedDest])
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Cloned. ${this.successNote}`,
          type: 'success',
          timeout: 8000
        })
        this.autoCloseTimer = setTimeout(() => {
          this.dialog = false
        }, 2600)
      } catch (error) {
        console.error('runClone', error)
        const mapped = this.mapError(error)

        this.errorMessage = mapped.message
        this.errorAction = mapped.action
        this.step = mapped.action === 'partial' ? 'partial' : 'error'
      }
    },
    submit () {
      if (!this.source || !this.selectedDest.length) {
        return
      }

      this.submittedDest = [...this.selectedDest]
      this.step = 'loading'
      this.runClone()
    },
    retry () {
      this.step = 'loading'
      this.runClone()
    },
    backToSelection () {
      this.step = 'select'
    }
  }
}
</script>

<style scoped>
.clone-trigger {
  background-color: #1a1a1a !important;
  color: #fff !important;
  letter-spacing: .3px;
  text-transform: uppercase;
  font-weight: 700;
}
.clone-card {
  padding: 28px 30px;
  border-radius: 16px !important;
  max-height: 86vh;
  overflow: auto;
}
.clone-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 22px;
}
.clone-title {
  font-weight: 700;
  font-size: 19px;
  color: #1a1a1a;
  margin-bottom: 4px;
}
.clone-subtitle {
  font-size: 13px;
  color: #8a8a86;
}
.clone-close {
  cursor: pointer;
  color: #999 !important;
}
.clone-warning {
  display: flex;
  align-items: flex-start;
  background-color: #fdf3e0;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #6b4d0e;
  margin-bottom: 20px;
}
.clone-section {
  margin-bottom: 20px;
}
.clone-note {
  font-size: 12.5px;
  color: #ad6800;
  margin-bottom: 16px;
}
.clone-section-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
}
.clone-label {
  font-weight: 700;
  font-size: 11px;
  letter-spacing: .5px;
  color: #8a8a86;
}
.clone-count {
  font-weight: 600;
  font-size: 11px;
  color: #b7b5af;
}
.clone-dest-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  max-height: 220px;
  overflow: auto;
  border: 1px solid rgba(0, 0, 0, .06);
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 14px;
}
.clone-dest-empty {
  grid-column: 1 / -1;
  padding: 12px;
  text-align: center;
  color: #9c9c9c;
  font-size: 13px;
}
.clone-dest-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.clone-dest-row.selected {
  background-color: #eef6f1;
}
.clone-checkbox {
  width: 17px;
  height: 17px;
  border-radius: 4px;
  border: 1.5px solid #c9c7c0;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.clone-checkbox.checked {
  background-color: #17976f;
  border-color: #17976f;
}
.clone-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.clone-submit {
  background-color: #1a1a1a !important;
  color: #fff !important;
}
.clone-submit.v-btn--disabled {
  background-color: #e7e5df !important;
  color: #a9a7a0 !important;
}
.clone-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 4px;
}
.clone-loading-label {
  font-weight: 700;
  font-size: 15px;
  color: #1a1a1a;
  margin-top: 18px;
}
.clone-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 4px 4px;
}
.clone-badge {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.clone-badge-success {
  background-color: #e6f6ee;
}
.clone-badge-error {
  background-color: #fbeae8;
}
.clone-badge-partial {
  background-color: #fdf3e0;
}
.clone-result-title {
  font-weight: 700;
  font-size: 19px;
  color: #1a1a1a;
  margin-bottom: 6px;
}
.clone-result-subtitle {
  font-size: 13px;
  line-height: 1.5;
  color: #8a8a86;
  margin-bottom: 20px;
}
.clone-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 22px;
}
.clone-chip {
  background-color: #f1f0eb;
  border-radius: 999px;
  padding: 7px 12px;
  font-weight: 600;
  font-size: 12.5px;
  color: #1a1a1a;
}
.clone-auto-close {
  font-weight: 500;
  font-size: 11.5px;
  color: #b7b5af;
  margin-bottom: 16px;
}
.clone-error-actions {
  display: flex;
  gap: 10px;
}
</style>
